import React, {useCallback, useEffect, useRef} from 'react';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';
import {ActiveToastMessages} from '../../graphql/queries/activeToastMessages';
import {
    buildActiveToastMessageFilter,
    isToastMessageCurrentlyDisplayed,
    nextVisibilityBoundary,
} from '../../utils/toastMessageVisibility';
import {SYSTEM_ANNOUNCEMENTS_CONTAINER_ID} from './constants';
import SystemAnnouncementToast from './SystemAnnouncementToast';

export const SYSTEM_ANNOUNCEMENTS_POLL_INTERVAL = 300000;

/**
 * Backend message ids are plain numbers ("1", "2", ...) — exactly the id
 * space react-toastify uses for auto-generated toastIds in other containers.
 * A collision makes the other toast unclosable, so namespace ours.
 */
const toastIdForMessage = (messageId: string) => `system-announcement-${messageId}`;

interface ActiveToastMessage {
    id: string;
    message?: string | null;
    fromDate: string;
    toDate: string;
    status: string;
}

/**
 * Headless poller that surfaces active system announcements (toast messages
 * from the backend: announcements / maintenance info) to every user as
 * persistent stacked toasts.
 *
 * Dismissal is intentionally in-memory only: a closed announcement stays
 * closed for this browser session and comes back after a reload/login while
 * still valid. The dismissed-set check is what prevents re-showing on poll —
 * the toastId dedupe only holds while a toast is on screen.
 *
 * Polling alone cannot hide/show messages on time: identical poll results
 * keep the same Apollo data reference, so the data effect never re-fires
 * while a fromDate/toDate passes mid-window. syncToasts therefore reschedules
 * itself for the exact next boundary (and for midnight, when the day-granular
 * server filter must be refreshed).
 */
const SystemAnnouncements = () => {
    const dismissedIdsRef = useRef<Set<string>>(new Set());
    const shownIdsRef = useRef<Set<string>>(new Set());
    const filterDayRef = useRef(moment().format('YYYY-MM-DD'));
    const messagesRef = useRef<ActiveToastMessage[]>([]);
    const boundaryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const {data, refetch} = useQuery(ActiveToastMessages, {
        variables: {filter: buildActiveToastMessageFilter()},
        pollInterval: SYSTEM_ANNOUNCEMENTS_POLL_INTERVAL,
        fetchPolicy: 'network-only',
    });

    const syncToasts = useCallback(function sync() {
        // The day-granular server filter freezes at mount — refresh it once the
        // date rolls over so long-lived tabs still receive tomorrow's messages.
        const today = moment().format('YYYY-MM-DD');
        if (today !== filterDayRef.current) {
            filterDayRef.current = today;
            refetch({filter: buildActiveToastMessageFilter()});
        }

        const messages = messagesRef.current;
        const validMessages = messages.filter(msg => msg?.id && isToastMessageCurrentlyDisplayed(msg));
        const validIds = new Set(validMessages.map(msg => msg.id));

        // Drop toasts whose message expired or was deactivated mid-session.
        shownIdsRef.current.forEach(id => {
            if (!validIds.has(id)) {
                toast.dismiss({id: toastIdForMessage(id), containerId: SYSTEM_ANNOUNCEMENTS_CONTAINER_ID});
                shownIdsRef.current.delete(id);
            }
        });

        validMessages.forEach(msg => {
            if (dismissedIdsRef.current.has(msg.id)) return;

            const content = <SystemAnnouncementToast message={msg.message ?? ''}/>;

            if (shownIdsRef.current.has(msg.id)) {
                // Already on screen — keep the content in sync with edits.
                toast.update(toastIdForMessage(msg.id), {containerId: SYSTEM_ANNOUNCEMENTS_CONTAINER_ID, render: content});
                return;
            }

            toast(content, {
                toastId: toastIdForMessage(msg.id),
                containerId: SYSTEM_ANNOUNCEMENTS_CONTAINER_ID,
                className: 'system-announcement-toast',
                onClose: (removedByUser) => {
                    if (removedByUser === true) {
                        dismissedIdsRef.current.add(msg.id);
                    }
                    shownIdsRef.current.delete(msg.id);
                },
            });
            shownIdsRef.current.add(msg.id);
        });

        // Re-run exactly when the next message boundary is crossed, or at
        // midnight for the filter rollover — whichever comes first. The +1s
        // makes sure the boundary (inclusive) is truly behind us.
        if (boundaryTimerRef.current) clearTimeout(boundaryTimerRef.current);
        const now = moment();
        const boundary = nextVisibilityBoundary(messages, now);
        const nextMidnight = now.clone().add(1, 'day').startOf('day');
        const nextRun = boundary && boundary.isBefore(nextMidnight) ? boundary : nextMidnight;
        boundaryTimerRef.current = setTimeout(sync, Math.max(nextRun.diff(now) + 1000, 1000));
    }, [refetch]);

    useEffect(() => {
        if (!data) return;
        const messages: ActiveToastMessage[] = _.get(data, 'toastMessageList.toastMessages', []) ?? [];
        messagesRef.current = messages.filter(Boolean);
        syncToasts();
    }, [data, syncToasts]);

    useEffect(() => () => {
        if (boundaryTimerRef.current) clearTimeout(boundaryTimerRef.current);
    }, []);

    return null;
};

export default SystemAnnouncements;
